import {computed, Injectable, signal} from '@angular/core';
import {CustomerInteraction} from '../models/customer-interaction.model';

export interface CustomerInteractionContext {
  orderId: string;
  propertyId: string;
}

@Injectable({
  providedIn: 'root',
})
export class CustomerInteractionStoreService {
  private readonly customerInteractions = signal<readonly CustomerInteraction[]>([]);
  private readonly interactionContext = signal<CustomerInteractionContext | null>(null);

  /** Order and property the loaded interactions belong to, used for messaging API calls. */
  public readonly context = this.interactionContext.asReadonly();

  public readonly openCustomerInteractions = computed(() => (
    this.customerInteractions().filter((interaction) => interaction.status === 'OPEN')
  ));

  public readonly openCustomerInteractionCount = computed(() => (
    this.openCustomerInteractions().length
  ));

  /** Active interactions shown in the main chat list; closed interactions are listed separately. */
  public readonly visibleCustomerInteractions = computed(() => (
    [...this.customerInteractions()]
      .filter((interaction) => interaction.status !== 'CLOSED')
      .sort((left, right) => this.toTime(right.date) - this.toTime(left.date))
  ));

  /** Closed interactions, shown collapsed as past chats at the end of the chat list. */
  public readonly closedCustomerInteractions = computed(() => (
    [...this.customerInteractions()]
      .filter((interaction) => interaction.status === 'CLOSED')
      .sort((left, right) => this.toTime(right.date) - this.toTime(left.date))
  ));

  public setCustomerInteractions(customerInteractions: readonly CustomerInteraction[]): void {
    this.customerInteractions.set(customerInteractions);
  }

  public setContext(context: CustomerInteractionContext): void {
    this.interactionContext.set(context);
  }

  /**
   * Replaces an interaction with its updated version or prepends a newly created one.
   *
   * @param interaction Interaction returned by the backend after a write operation.
   */
  public upsertCustomerInteraction(interaction: CustomerInteraction): void {
    if (!interaction.id) {
      return;
    }

    this.customerInteractions.update((interactions) => {
      const index = interactions.findIndex((existing) => existing.id === interaction.id);
      if (index === -1) {
        return [interaction, ...interactions];
      }

      const next = [...interactions];
      next[index] = interaction;
      return next;
    });
  }

  public clear(): void {
    this.customerInteractions.set([]);
    this.interactionContext.set(null);
  }

  private toTime(value: string | undefined): number {
    const time = value ? new Date(value).getTime() : Number.NaN;

    return Number.isNaN(time) ? 0 : time;
  }
}
